import { toast } from '@components/Toaster'
import { SessionCommentType } from '@graph/schemas'
import {
	Box,
	IconSolidArrowCircleRight,
	IconSolidDotsHorizontal,
	IconSolidLink,
	IconSolidTrash,
	Menu,
	Stack,
	Text,
} from '@highlight-run/ui/components'
import { vars } from '@highlight-run/ui/vars'
import { PlayerSearchParameters } from '@pages/Player/PlayerHook/utils'
import {
	ParsedSessionComment,
	useReplayerContext,
} from '@pages/Player/ReplayerContext'
import { onGetLinkWithTimestamp } from '@pages/Player/SessionShareButton/utils/utils'
import moment from 'moment'
import React from 'react'

import { AdminAvatar, Avatar } from '@/components/Avatar/Avatar'
import {
	useDeleteComment,
	useNavigateToComment,
} from '@/components/Comment/utils/utils'

interface Props {
	comment: ParsedSessionComment
	isReply?: boolean
}

const SessionCommentHeader: React.FC<Props> = ({ comment, isReply }) => {
	const { session } = useReplayerContext()
	const navigateToComment = useNavigateToComment(comment)
	const deleteComment = useDeleteComment(comment)

	const getCommentLink = () => {
		const url = onGetLinkWithTimestamp(comment.timestamp || 0)
		url.searchParams.set(PlayerSearchParameters.commentId, comment.id)
		return url
	}

	const authorName =
		comment.type === SessionCommentType.Feedback
			? comment.metadata?.name ||
				comment.metadata?.email?.split('@')[0] ||
				'Anonymous'
			: comment.author?.name || comment.author?.email.split('@')[0]

	const timeAgo = moment(comment.created_at).fromNow()

	return (
		<Box>
			<Box
				display="flex"
				alignItems="center"
				justifyContent="space-between"
			>
				<Stack direction="row" gap="4" alignItems="center">
					{comment.type === SessionCommentType.Feedback ? (
						<Avatar />
					) : (
						<AdminAvatar
							adminInfo={{
								name: comment.author?.name ?? undefined,
								photo_url: comment.author?.photo_url,
								email: comment.author?.email,
							}}
							size={20}
						/>
					)}
					<Text size="small" color="strong" lines="1">
						{authorName}
					</Text>
					<Text size="small" color="moderate" lines="1">
						{timeAgo}
					</Text>
				</Stack>

				{!isReply && (
					<Box>
						<Menu placement="bottom-end">
							<Menu.Button
								onClick={(
									e: React.MouseEvent<HTMLButtonElement>,
								) => {
									e.stopPropagation()
								}}
								icon={
									<IconSolidDotsHorizontal
										size={14}
										color={vars.color.n11}
									/>
								}
								emphasis="low"
								kind="secondary"
								size="xSmall"
							/>
							<Menu.List>
								<Menu.Item onClick={navigateToComment}>
									<Stack
										gap="4"
										direction="row"
										align="center"
									>
										<IconSolidArrowCircleRight />
										Go to timestamp
									</Stack>
								</Menu.Item>
								<Menu.Item
									onClick={() => {
										const url = getCommentLink()
										toast.success('Copied link!')
										navigator.clipboard.writeText(url.href)
									}}
								>
									<Stack
										gap="4"
										direction="row"
										align="center"
									>
										<IconSolidLink />
										Copy link
									</Stack>
								</Menu.Item>
								<Menu.Item onClick={deleteComment}>
									<Stack
										gap="4"
										direction="row"
										align="center"
									>
										<IconSolidTrash />
										Delete comment
									</Stack>
								</Menu.Item>
							</Menu.List>
						</Menu>
					</Box>
				)}
			</Box>
		</Box>
	)
}

export default SessionCommentHeader
